import React, { useMemo, useState, useEffect } from 'react';
import { Tree, Empty } from 'antd';
import type { DataNode } from 'antd/es/tree';
import type { OntologyGraphTree } from '../../types/opm';

interface OntologyModelPanelProps {
  treeData: OntologyGraphTree;
  selectedLayerId: string;
  onSelectLayer: (layerId: string) => void;
}

const collectLayerKeys = (layers: OntologyGraphTree, acc: string[] = []) => {
  layers.forEach(layer => {
    acc.push(layer.layerId);
    if (layer.children?.length) {
      collectLayerKeys(layer.children, acc);
    }
  });
  return acc;
};

const buildTreeNodes = (layers: OntologyGraphTree): DataNode[] =>
  layers.map(layer => {
    const nodeCount = layer.graphData?.nodes?.length || 0;
    return {
      key: layer.layerId,
      title: (
        <div className="ontology-tree-node">
          <OntologyTreeIcon />
          <span className="ontology-tree-name">{layer.layerName}</span>
          {/* <span className="ontology-tree-meta">节点 {nodeCount}</span> */}
        </div>
      ),
      children: layer.children?.length ? buildTreeNodes(layer.children) : undefined,
    };
  });

const OntologyTreeIcon: React.FC = () => (
  <svg
    className="ontology-tree-icon"
    viewBox="0 0 1024 1024"
    xmlns="http://www.w3.org/2000/svg"
    width={16}
    height={16}
  >
    <path
      d="M870.4 711.111111c-28.444444 0-56.888889 8.533333-79.644444 22.755556l-162.133334-156.444445c25.6-34.133333 42.666667-76.8 42.666667-122.311111 0-42.666667-14.222222-82.488889-36.977778-113.777778l93.866667-99.555555c17.066667 8.533333 36.977778 17.066667 56.888889 17.066666 62.577778 0 113.777778-51.2 113.777777-113.777777s-51.2-113.777778-113.777777-113.777778-113.777778 51.2-113.777778 113.777778c0 22.755556 5.688889 39.822222 17.066667 56.888889l-93.866667 99.555555c-31.288889-28.444444-73.955556-45.511111-122.311111-45.511111-48.355556 0-93.866667 17.066667-130.844445 48.355556L258.844444 227.555556c8.533333-17.066667 14.222223-36.977778 14.222223-56.888889 0-62.577778-51.2-113.777778-113.777778-113.777778s-113.777778 51.2-113.777778 113.777778 51.2 113.777778 113.777778 113.777777c22.755556 0 42.666667-5.688889 59.733333-17.066666l88.177778 79.644444c-22.755556 31.288889-34.133333 68.266667-34.133333 108.088889 0 51.2 19.911111 96.711111 51.2 130.844445l-59.733334 62.577777c-22.755556-14.222222-48.355556-22.755556-76.8-22.755555-79.644444 0-142.222222 62.577778-142.222222 142.222222s62.577778 142.222222 142.222222 142.222222 142.222223-62.577778 142.222223-142.222222c0-28.444444-8.533333-56.888889-25.6-79.644444l59.733333-65.422223c31.288889 19.911111 68.266667 31.288889 105.244444 31.288889 42.666667 0 82.488889-14.222222 113.777778-36.977778l164.977778 156.444445c-11.377778 22.755556-19.911111 51.2-19.911111 79.644444 0 79.644444 62.577778 142.222222 142.222222 142.222223s142.222222-62.577778 142.222222-142.222223-62.577778-142.222222-142.222222-142.222222z m-85.333333-625.777778c31.288889 0 56.888889 25.6 56.888889 56.888889s-25.6 56.888889-56.888889 56.888889-56.888889-25.6-56.888889-56.888889 25.6-56.888889 56.888889-56.888889z m-682.666667 85.333334c0-31.288889 25.6-56.888889 56.888889-56.888889s56.888889 25.6 56.888889 56.888889-25.6 56.888889-56.888889 56.888889-56.888889-25.6-56.888889-56.888889z m85.333333 682.666666c-48.355556 0-85.333333-36.977778-85.333333-85.333333s36.977778-85.333333 85.333333-85.333333c14.222222 0 25.6 2.844444 36.977778 8.533333l39.822222 36.977778c5.688889 11.377778 8.533333 25.6 8.533334 39.822222 0 48.355556-36.977778 85.333333-85.333334 85.333333z m284.444445-256c-79.644444 0-142.222222-62.577778-142.222222-142.222222s62.577778-142.222222 142.222222-142.222222 142.222222 62.577778 142.222222 142.222222-62.577778 142.222222-142.222222 142.222222z m398.222222 341.333334c-48.355556 0-85.333333-36.977778-85.333333-85.333334s36.977778-85.333333 85.333333-85.333333 85.333333 36.977778 85.333333 85.333333-36.977778 85.333333-85.333333 85.333334z"
      fill="#1890ff"
    />
  </svg>
);

const OntologyModelPanel: React.FC<OntologyModelPanelProps> = ({
  treeData,
  selectedLayerId,
  onSelectLayer,
}) => {
  const treeNodes = useMemo(() => buildTreeNodes(treeData), [treeData]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>(() =>
    collectLayerKeys(treeData, [])
  );

  useEffect(() => {
    setExpandedKeys(collectLayerKeys(treeData, []));
  }, [treeData]);

  return (
    <div className="ontology-panel-card ontology-model-panel">
      <div className="ontology-panel-header">
        <h3>本体模型</h3>
      </div>
      <div className="ontology-tree-container">
        {treeNodes.length > 0 ? (
          <Tree
            blockNode
            showLine={false}
            treeData={treeNodes}
            selectedKeys={selectedLayerId ? [selectedLayerId] : []}
            expandedKeys={expandedKeys}
            onExpand={keys => setExpandedKeys(keys as string[])}
            autoExpandParent={false}
            onSelect={keys => {
              const key = keys[0] as string;
              if (key) {
                onSelectLayer(key);
              }
            }}
          />
        ) : (
          <Empty description="暂无本体图" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </div>
    </div>
  );
};

export default OntologyModelPanel;
